import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Calendar,
  ArrowRight,
  Search,
  X,
  Layers,
  Code,
  Smartphone,
  LayoutTemplate,
  Terminal,
  Sparkles,
  TrendingUp,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Star,
  Flame,
  Lightbulb,
  Clock,
  BookOpen,
  ArrowUpRight,
  Compass,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { blogService, BlogPost } from '../services/blogService';
import { SignatureReveal } from '../components/Animations';
import { BlogListingSkeleton } from '../components/Skeleton';
import { useSeo } from '../hooks/useSeo';
import { useTheme } from '../context/ThemeContext';
import { SharedCTA } from '../components/SharedCTA';
import { cn } from '../lib/utils';

interface BlogCategory {
  id: string;
  name: string;
  icon: any;
}

const blogCategories: BlogCategory[] = [
  { id: 'all', name: 'All Articles', icon: Layers },
  { id: 'web-dev', name: 'Web Dev', icon: Code },
  { id: 'mobile-app', name: 'Mobile Apps', icon: Smartphone },
  { id: 'ai', name: 'AI & Trends', icon: Sparkles },
  { id: 'ui-ux', name: 'UI / UX Design', icon: LayoutTemplate },
  { id: 'enterprise', name: 'Enterprise & Cloud', icon: Terminal },
  { id: 'marketing', name: 'SEO & Growth', icon: TrendingUp },
];

const getBlogCategoryInfo = (blog: BlogPost): { id: string; name: string } => {
  const title = (blog.title || '').toLowerCase();
  const text = `${title} ${blog.excerpt || ''}`.toLowerCase();

  // 1. AI & Machine Learning
  if (
    title.includes('ai ') ||
    title.includes('ai:') ||
    title.includes('ai vs') ||
    title.includes('artificial intelligence') ||
    title.includes('chatbot') ||
    title.includes('generative ai') ||
    title.includes('detect fraudulent') ||
    title.includes('adaptive learning') ||
    title.includes('code assistants') ||
    text.includes('conversational ai')
  ) {
    return { id: 'ai', name: 'AI & Trends' };
  }

  // 2. UI / UX Design
  if (
    title.includes('ui/ux') ||
    title.includes('ux design') ||
    title.includes('user experience') ||
    title.includes('app design') ||
    title.includes('psychology behind great user')
  ) {
    return { id: 'ui-ux', name: 'UI / UX Design' };
  }

  // 3. Mobile Apps
  if (
    title.includes('mobile app') ||
    title.includes('android mobile') ||
    title.includes('android app') ||
    title.includes('ios app') ||
    title.includes('flutter') ||
    title.includes('react native')
  ) {
    return { id: 'mobile-app', name: 'Mobile Apps' };
  }

  // 4. SEO & Digital Marketing
  if (
    title.includes('digital marketing') ||
    title.includes('seo and conversions') ||
    title.includes('seo') ||
    title.includes('marketing')
  ) {
    return { id: 'marketing', name: 'SEO & Growth' };
  }

  // 5. Enterprise & Cloud
  if (
    title.includes('erp') ||
    title.includes('crm') ||
    title.includes('odoo') ||
    title.includes('zoho') ||
    title.includes('zero trust') ||
    title.includes('cybersecurity') ||
    title.includes('qa & testing') ||
    title.includes('testing') ||
    title.includes('support & maintenance') ||
    title.includes('maintenance & support') ||
    title.includes('business process automation') ||
    title.includes('inventory management') ||
    title.includes('digital transformation') ||
    title.includes('it service models') ||
    title.includes('custom software development') ||
    title.includes('cloud') ||
    title.includes('devops') ||
    title.includes('docker')
  ) {
    return { id: 'enterprise', name: 'Enterprise & Cloud' };
  }

  // 6. Web Development (default & explicit)
  return { id: 'web-dev', name: 'Web Dev' };
};

const formatBlogDate = (dateStr: string | undefined | null, pattern = 'MMM dd, yyyy'): string => {
  if (!dateStr) return 'Recent';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Recent';
    return format(d, pattern);
  } catch (_) {
    return 'Recent';
  }
};

const getReadingTime = (blog: BlogPost): string => {
  const content = `${blog.title || ''} ${blog.excerpt || ''}`;
  const words = content.trim().split(/\s+/).length;
  const time = Math.max(3, Math.min(7, Math.ceil(words / 15)));
  return `${time} min read`;
};

interface GlobalBlogCache {
  blogs: BlogPost[];
  categoryCounts: Record<string, number>;
  totalPages: number;
  cacheByQuery: Record<string, any>;
  activeCategory: string;
  currentPage: number;
  searchTerm: string;
}

const CACHE_STORAGE_KEY = 'prince_blog_cache_v2';

const globalBlogCache: GlobalBlogCache = {
  blogs: [],
  categoryCounts: { all: 0 },
  totalPages: 1,
  cacheByQuery: {},
  activeCategory: 'all',
  currentPage: 1,
  searchTerm: '',
};

function getInitialBlogData(): { blogs: BlogPost[]; categoryCounts: Record<string, number>; totalPages: number } {
  // 1. In-memory global cache if already loaded
  if (globalBlogCache.blogs && globalBlogCache.blogs.length > 0) {
    return {
      blogs: globalBlogCache.blogs,
      categoryCounts: globalBlogCache.categoryCounts,
      totalPages: globalBlogCache.totalPages,
    };
  }

  // 2. Server-rendered initial data on window if present
  if (typeof window !== 'undefined' && (window as any).__INITIAL_BLOGS__) {
    try {
      const data = (window as any).__INITIAL_BLOGS__;
      if (data && Array.isArray(data.blogs) && data.blogs.length > 0) {
        globalBlogCache.blogs = data.blogs;
        if (data.categoryCounts) globalBlogCache.categoryCounts = data.categoryCounts;
        if (data.totalPages) globalBlogCache.totalPages = data.totalPages;
        return {
          blogs: data.blogs,
          categoryCounts: data.categoryCounts || { all: data.blogs.length },
          totalPages: data.totalPages || 1,
        };
      }
    } catch (_) {}
  }

  // 3. Persistent browser storage (sessionStorage & localStorage)
  if (typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem(CACHE_STORAGE_KEY) || localStorage.getItem(CACHE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && Array.isArray(parsed.blogs) && parsed.blogs.length > 0) {
          globalBlogCache.blogs = parsed.blogs;
          if (parsed.categoryCounts) globalBlogCache.categoryCounts = parsed.categoryCounts;
          if (parsed.totalPages) globalBlogCache.totalPages = parsed.totalPages;
          return {
            blogs: parsed.blogs,
            categoryCounts: parsed.categoryCounts || { all: parsed.blogs.length },
            totalPages: parsed.totalPages || 1,
          };
        }
      }
    } catch (_) {}
  }

  return {
    blogs: [],
    categoryCounts: { all: 0 },
    totalPages: 1,
  };
}

export default function Blog() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  useSeo({
    title: 'Tech Insights & Guides | Prince IT Solution Blog',
    description: 'Explore technology insights, web development tutorials, mobile app guides, and IT industry trends on the Prince IT Solution blog.',
    keywords: 'technology blog, web development guides, mobile app tutorials, IT insights Surat, software engineering blog',
    canonicalUrl: 'https://www.princeitsolution.com/blog'
  });

  const initialData = getInitialBlogData();
  const [blogs, setBlogs] = useState<BlogPost[]>(() => initialData.blogs);
  const [isInitialLoading, setIsInitialLoading] = useState(() => initialData.blogs.length === 0);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(() => globalBlogCache.currentPage || 1);
  const [totalPages, setTotalPages] = useState(() => initialData.totalPages || 1);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(() => initialData.categoryCounts);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [searchTerm, setSearchTerm] = useState(() => globalBlogCache.searchTerm || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => globalBlogCache.searchTerm || '');
  const [activeCategory, setActiveCategory] = useState(() => globalBlogCache.activeCategory || 'all');
  const fetchIdRef = useRef(0);

  // Search & filter bar scroll target ref
  const searchFilterRef = useRef<HTMLDivElement>(null);

  // Category scroll navigation & mouse drag-to-scroll refs
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const hasMovedRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    startXRef.current = e.pageX - categoryScrollRef.current.offsetLeft;
    startScrollLeftRef.current = categoryScrollRef.current.scrollLeft;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !categoryScrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - categoryScrollRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1.5;
    if (Math.abs(walk) > 4) {
      hasMovedRef.current = true;
    }
    categoryScrollRef.current.scrollLeft = startScrollLeftRef.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    isDraggingRef.current = false;
    setTimeout(() => {
      hasMovedRef.current = false;
    }, 50);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (categoryScrollRef.current && Math.abs(e.deltaY) > 0) {
      categoryScrollRef.current.scrollLeft += e.deltaY;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      globalBlogCache.searchTerm = searchTerm;
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };
    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  const perPage = isMobile ? 6 : 9;

  const fetchBlogs = async () => {
    const fetchId = ++fetchIdRef.current;
    const cacheKey = `${activeCategory}_${currentPage}_${debouncedSearch}_${perPage}`;

    // If cached, display cached data immediately without any loading flicker
    if (globalBlogCache.cacheByQuery[cacheKey]) {
      const cached = globalBlogCache.cacheByQuery[cacheKey];
      setBlogs(cached.data);
      if (cached.category_counts) {
        setCategoryCounts(cached.category_counts);
      }
      setTotalPages(cached.pagination?.last_page || 1);
      setIsInitialLoading(false);
    }

    try {
      setIsFetching(true);
      setError(null);
      const response = await blogService.getBlogs(currentPage, perPage, activeCategory, debouncedSearch);

      if (fetchId !== fetchIdRef.current) return;

      if (response.status && Array.isArray(response.data)) {
        globalBlogCache.blogs = response.data;
        globalBlogCache.cacheByQuery[cacheKey] = response;
        setBlogs(response.data);
        if (response.category_counts) {
          globalBlogCache.categoryCounts = response.category_counts;
          setCategoryCounts(response.category_counts);
        }
        if (response.pagination?.last_page) {
          globalBlogCache.totalPages = response.pagination.last_page;
          setTotalPages(response.pagination.last_page);
        }

        // Persist default landing view (page 1, all categories, no search) for instant zero-skeleton refreshes
        if (activeCategory === 'all' && currentPage === 1 && !debouncedSearch && response.data.length > 0) {
          try {
            const cachePayload = {
              blogs: response.data,
              categoryCounts: response.category_counts || globalBlogCache.categoryCounts,
              totalPages: response.pagination?.last_page || 1,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cachePayload));
            localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cachePayload));
          } catch (_) {}
        }
      } else {
        if (blogs.length === 0 && globalBlogCache.blogs.length === 0) {
          setError('Failed to load blogs');
        }
      }
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      if (blogs.length === 0 && globalBlogCache.blogs.length === 0) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsFetching(false);
        setIsInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    globalBlogCache.activeCategory = activeCategory;
    globalBlogCache.currentPage = currentPage;
    fetchBlogs();
  }, [currentPage, perPage, activeCategory, debouncedSearch]);

  const handleCategoryClick = (catId: string, e?: React.MouseEvent<HTMLButtonElement>, scrollToTop = false) => {
    if (hasMovedRef.current) return;
    setActiveCategory(catId);
    setCurrentPage(1);
    globalBlogCache.activeCategory = catId;
    globalBlogCache.currentPage = 1;

    // Immediate optimistic cache lookup if available
    const nextCacheKey = `${catId}_1_${debouncedSearch}_${perPage}`;
    if (globalBlogCache.cacheByQuery[nextCacheKey]) {
      const cached = globalBlogCache.cacheByQuery[nextCacheKey];
      setBlogs(cached.data);
      if (cached.category_counts) {
        setCategoryCounts(cached.category_counts);
      }
      setTotalPages(cached.pagination?.last_page || 1);
    }

    if (e?.currentTarget) {
      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    if (scrollToTop) {
      if (searchFilterRef.current) {
        searchFilterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const target = document.getElementById('blog-search-filter-bar');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    globalBlogCache.searchTerm = value;
    globalBlogCache.currentPage = 1;
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=2072';
  };

  const validBlogs = (Array.isArray(blogs) ? blogs : []).filter((b): b is BlogPost => Boolean(b && typeof b === 'object' && b.slug));
  const editorsPick = validBlogs.length > 0 ? validBlogs[0] : null;
  const trendingPosts = validBlogs.length > 1 ? validBlogs.slice(1, 5) : validBlogs;

  return (
    <div className={cn(
      "min-h-screen pt-32 sm:pt-36 lg:pt-40 pb-20 relative transition-colors duration-300",
      isLight ? "bg-[#f8fafc] text-slate-900" : "bg-[#030712] text-slate-100"
    )}>
      {/* Editorial Ambient Background Glow & Geometric Accents */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Subtle Tech Dot Matrix */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-500",
            isLight ? "opacity-[0.06]" : "opacity-[0.16]"
          )}
          style={{
            backgroundImage: isLight
              ? 'radial-gradient(circle at center, var(--color-brand-600) 1px, transparent 1px)'
              : 'radial-gradient(circle at center, var(--color-brand-400) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />

        {/* Soft Radial Ambient Aura Blurs */}
        <div className={cn(
          "absolute -top-24 right-0 w-[650px] h-[650px] rounded-full blur-[140px] transition-all",
          isLight ? "bg-sky-200/40" : "bg-brand-500/10"
        )} />
        <div className={cn(
          "absolute top-1/4 -left-20 w-[550px] h-[550px] rounded-full blur-[160px] transition-all",
          isLight ? "bg-indigo-100/60" : "bg-brand-600/10"
        )} />
        <div className={cn(
          "absolute top-2/3 right-10 w-[600px] h-[600px] rounded-full blur-[150px] transition-all",
          isLight ? "bg-brand-100/40" : "bg-brand-600/8"
        )} />
      </div>

      <div className="max-w-[92rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* ── HERO SECTION: TEXT-ONLY, CLEAN, MINIMAL & CENTERED ── */}
        <div className="text-center max-w-4xl mx-auto mb-10 sm:mb-12 pt-2 sm:pt-4">
          <SignatureReveal delay={0.05}>
            <div className={cn(
              "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-semibold text-xs tracking-wider uppercase border backdrop-blur-md mb-4 sm:mb-5 transition-all",
              isLight
                ? "bg-white/90 border-brand-200/80 text-brand-700 shadow-xs shadow-brand-500/5"
                : "bg-brand-950/40 border-brand-500/30 text-brand-400 shadow-xs shadow-brand-500/10"
            )}>
              <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
              <span>PRINCE IT SOLUTION · BLOG</span>
            </div>
          </SignatureReveal>

          <SignatureReveal delay={0.12}>
            <h1 className={cn(
              "text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold tracking-tight leading-[1.14] mb-4 sm:mb-5",
              isLight ? "text-slate-900" : "text-white"
            )}>
              Ideas, Insights & What’s Next{' '}
              <span className="bg-gradient-to-r from-brand-600 via-sky-500 to-brand-400 bg-clip-text text-transparent">
                in Tech
              </span>
            </h1>
          </SignatureReveal>

          <SignatureReveal delay={0.2}>
            <p className={cn(
              "text-base sm:text-lg font-normal leading-relaxed max-w-2xl mx-auto",
              isLight ? "text-slate-600" : "text-slate-300/90"
            )}>
              Curated articles, practical engineering guides, and expert perspectives on modern software development, AI, and digital growth.
            </p>
          </SignatureReveal>
        </div>

        {/* ── SEARCH & CATEGORY FILTER BAR ── */}
        <div
          ref={searchFilterRef}
          id="blog-search-filter-bar"
          className="mb-10 sm:mb-12 max-w-7xl mx-auto scroll-mt-24 lg:scroll-mt-28"
        >
          <div className={cn(
            "flex flex-col md:flex-row md:items-center gap-3 p-2 rounded-2xl md:rounded-full border backdrop-blur-xl transition-all duration-300",
            isLight
              ? "bg-white/90 border-slate-200/90 shadow-md shadow-slate-200/40"
              : "bg-[#0B132B]/85 border-slate-800 shadow-[0_12px_35px_rgba(0,0,0,0.45)]"
          )}>
            {/* Search Input Container */}
            <div className="relative w-full md:w-64 lg:w-72 shrink-0">
              <Search className={cn(
                "absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors",
                isLight ? "text-slate-400" : "text-slate-400"
              )} />
              <input
                type="text"
                placeholder="Search articles & topics..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className={cn(
                  "w-full pl-10 pr-9 py-2 rounded-full text-xs sm:text-sm font-medium transition-all focus:outline-none",
                  isLight
                    ? "bg-slate-100/80 border border-slate-200 text-slate-900 placeholder-slate-400 focus:bg-white focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                    : "bg-[#060B18] border border-white/10 text-white placeholder-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                )}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Desktop Vertical Divider */}
            <div className={cn(
              "hidden md:block w-px h-6 shrink-0",
              isLight ? "bg-slate-200" : "bg-slate-800"
            )} />

            {/* Category Filter Chips (Mouse Drag + Scrollable) */}
            <div
              ref={categoryScrollRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUpOrLeave}
              onMouseLeave={handleMouseUpOrLeave}
              onWheel={handleWheel}
              className={cn(
                "category-scrollbar no-scrollbar scrollbar-none flex items-center gap-1.5 overflow-x-auto w-full py-0.5 select-none flex-1 cursor-grab active:cursor-grabbing",
                "-mx-1 px-1 sm:mx-0 sm:px-0"
              )}
            >
              {blogCategories.map((cat) => {
                const Icon = cat.icon;
                const isActive = activeCategory === cat.id;
                const count = categoryCounts[cat.id] ?? 0;

                return (
                  <button
                    key={cat.id}
                    onClick={(e) => handleCategoryClick(cat.id, e)}
                    className={cn(
                      "px-3.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap transition-all duration-200 cursor-pointer shrink-0",
                      isActive
                        ? "bg-brand-600 text-white shadow-sm shadow-brand-500/30 scale-[1.02]"
                        : isLight
                          ? "bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-950 border border-slate-200/80"
                          : "bg-[#060B18] text-slate-300 hover:bg-white/10 hover:text-white border border-white/5"
                    )}
                  >
                    {Icon && (
                      <Icon className={cn(
                        "w-3.5 h-3.5",
                        isActive ? "text-white" : (isLight ? "text-slate-500" : "text-slate-400")
                      )} />
                    )}
                    <span>{cat.name}</span>
                    <span className={cn(
                      "px-1.5 py-0.2 rounded-full text-[10px] font-bold",
                      isActive
                        ? "bg-white/25 text-white"
                        : isLight
                          ? "bg-slate-200/80 text-slate-600"
                          : "bg-white/10 text-slate-400"
                    )}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT AREA + RIGHT SIDEBAR (NO LEFT SIDEBAR) ── */}
        {isInitialLoading && blogs.length === 0 ? (
          <BlogListingSkeleton isLight={isLight} />
        ) : error && blogs.length === 0 ? (
          <div className={cn(
            "text-center py-20 rounded-3xl border border-dashed p-8 max-w-xl mx-auto my-8",
            isLight ? "bg-white border-red-200" : "bg-[#0c1324]/50 border-red-900/40"
          )}>
            <p className="text-red-400 mb-4 font-medium">{error}</p>
            <button
              onClick={fetchBlogs}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer bg-brand-600 text-white hover:bg-brand-700 shadow-sm"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Try Again</span>
            </button>
          </div>
        ) : blogs.length === 0 && !isFetching ? (
          <div className={cn(
            "text-center py-16 sm:py-20 rounded-3xl border border-dashed p-8 my-8 transition-colors max-w-2xl mx-auto",
            isLight ? "bg-white/80 border-slate-300 shadow-sm" : "bg-[#0c1324]/40 border-white/10"
          )}>
            <Search className="w-12 h-12 mx-auto text-slate-400 mb-4 opacity-50" />
            <h3 className={cn("text-xl font-bold mb-2 font-display", isLight ? "text-slate-900" : "text-white")}>
              No articles found
            </h3>
            <p className={cn("text-sm max-w-md mx-auto mb-6 leading-relaxed", isLight ? "text-slate-500" : "text-slate-400")}>
              We couldn't find any articles matching "{debouncedSearch}". Try selecting another category or resetting filters.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setActiveCategory('all');
                setCurrentPage(1);
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer shadow-md bg-brand-600 text-white hover:bg-brand-700 shadow-brand-500/20"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset Filters</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8 items-start relative w-full">
            {/* ── MAIN ARTICLES FEED (3-COLUMN ON DESKTOP) ── */}
            <div className="flex-1 min-w-0 w-full space-y-6">
              {/* Section Header */}
              <div className={cn(
                "flex items-center justify-between pb-3 border-b",
                isLight ? "border-slate-200" : "border-slate-800"
              )}>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                  <h2 className={cn(
                    "text-base sm:text-lg font-display font-bold tracking-tight",
                    isLight ? "text-slate-900" : "text-white"
                  )}>
                    {activeCategory === 'all'
                      ? 'Latest Articles'
                      : `${blogCategories.find(c => c.id === activeCategory)?.name || 'Filtered'} Articles`}
                  </h2>
                </div>
                <span className={cn(
                  "px-2.5 py-0.5 rounded-full text-xs font-medium",
                  isLight ? "bg-slate-100 text-slate-600" : "bg-slate-800/80 text-slate-300"
                )}>
                  {blogs.length} {blogs.length === 1 ? 'article' : 'articles'}
                </span>
              </div>

              {/* Articles Grid (3-Column on Desktop, 2 on Tablet, 1 on Mobile) */}
              <div className={cn(
                "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-200",
                isFetching ? "opacity-75" : "opacity-100"
              )}>
                {blogs.map((blog, index) => {
                  const cat = getBlogCategoryInfo(blog);
                  const readTime = getReadingTime(blog);

                  return (
                    <motion.article
                      key={blog.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.04, 0.36), duration: 0.28 }}
                      className={cn(
                        "group relative flex flex-col h-full rounded-2xl border overflow-hidden transition-all duration-300",
                        isLight
                          ? "bg-white border-slate-200/85 shadow-xs hover:shadow-md hover:-translate-y-1 hover:border-brand-400/50"
                          : "bg-[#0B132B]/85 border-slate-800 shadow-[0_8px_24px_rgba(0,0,0,0.35)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.5)] hover:-translate-y-1 hover:border-brand-500/40"
                      )}
                    >
                      {/* Thumbnail Poster Image with Category Badge */}
                      <Link
                        to={`/blog/${blog.slug}`}
                        state={{ blog }}
                        className={cn(
                          "block relative aspect-[16/10] overflow-hidden shrink-0",
                          isLight ? "bg-slate-100" : "bg-slate-900"
                        )}
                      >
                        <img
                          src={blogService.getFeaturedImageUrl(blog.featured_image, blog.image_url)}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                          referrerPolicy="no-referrer"
                          onError={handleImageError}
                        />
                        <div className="absolute top-3 left-3 z-10">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCategoryClick(cat.id);
                            }}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider backdrop-blur-md transition-all cursor-pointer shadow-xs",
                              isLight
                                ? "bg-white/95 text-brand-700 border border-brand-200/80 hover:bg-brand-600 hover:text-white"
                                : "bg-slate-950/85 text-brand-400 border border-brand-500/30 hover:bg-brand-600 hover:text-white"
                            )}
                          >
                            {cat.name}
                          </button>
                        </div>
                      </Link>

                      {/* Article Details Container */}
                      <div className="p-5 flex-1 flex flex-col">
                        {/* Meta: Date & Read Time */}
                        <div className={cn(
                          "flex items-center justify-between text-[11px] font-medium mb-2.5",
                          isLight ? "text-slate-400" : "text-slate-400"
                        )}>
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3 h-3 text-brand-500" />
                            {blog.published_at ? format(new Date(blog.published_at), 'MMM dd, yyyy') : 'Recent'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-brand-400" />
                            {readTime}
                          </span>
                        </div>

                        {/* Article Title */}
                        <Link to={`/blog/${blog.slug}`} state={{ blog }} className="group/title block mb-2">
                          <h3 className={cn(
                            "font-display font-bold text-base leading-snug transition-colors line-clamp-2",
                            isLight
                              ? "text-slate-900 group-hover/title:text-brand-600"
                              : "text-white group-hover/title:text-brand-400"
                          )}>
                            {blog.title}
                          </h3>
                        </Link>

                        {/* Excerpt */}
                        <p className={cn(
                          "text-xs font-normal mb-5 line-clamp-2 leading-relaxed flex-1",
                          isLight ? "text-slate-600" : "text-slate-300/80"
                        )}>
                          {blog.excerpt}
                        </p>

                        {/* Card Footer: Read Article Link & Author */}
                        <div className={cn(
                          "mt-auto pt-3.5 border-t flex justify-between items-center",
                          isLight ? "border-slate-100" : "border-white/5"
                        )}>
                          <Link
                            to={`/blog/${blog.slug}`}
                            state={{ blog }}
                            className={cn(
                              "inline-flex items-center gap-1.5 font-bold text-[11px] uppercase tracking-wider transition-colors group/link",
                              isLight
                                ? "text-brand-600 hover:text-brand-700"
                                : "text-brand-400 hover:text-brand-300"
                            )}
                          >
                            <span>Read Article</span>
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                          </Link>

                          <span className={cn(
                            "text-[10px] font-medium",
                            isLight ? "text-slate-400" : "text-slate-400"
                          )}>
                            {blog.author || 'Prince IT Team'}
                          </span>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pt-8 pb-4 flex justify-center items-center gap-3">
                  <button
                    onClick={() => {
                      setCurrentPage(p => Math.max(1, p - 1));
                      window.scrollTo({ top: 350, behavior: 'smooth' });
                    }}
                    disabled={currentPage === 1}
                    className={cn(
                      "flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed",
                      isLight
                        ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-xs"
                        : "bg-[#0B132B] border-slate-800 text-white hover:bg-slate-800"
                    )}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Previous</span>
                  </button>

                  <div className={cn(
                    "flex items-center gap-1.5 text-xs font-semibold px-2",
                    isLight ? "text-slate-500" : "text-slate-400"
                  )}>
                    <span>Page</span>
                    <span className={cn(
                      "font-bold px-2.5 py-0.5 rounded-md",
                      isLight ? "bg-brand-50 text-brand-600" : "bg-brand-950/60 text-brand-400 border border-brand-500/20"
                    )}>
                      {currentPage}
                    </span>
                    <span>of {totalPages}</span>
                  </div>

                  <button
                    onClick={() => {
                      setCurrentPage(p => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 350, behavior: 'smooth' });
                    }}
                    disabled={currentPage === totalPages}
                    className={cn(
                      "flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed",
                      isLight
                        ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-xs"
                        : "bg-[#0B132B] border-slate-800 text-white hover:bg-slate-800"
                    )}
                  >
                    <span>Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* ── RIGHT SIDEBAR: TRENDING NOW + TECH ADVISORY ── */}
            <aside className="w-full lg:w-[320px] xl:w-[340px] shrink-0 lg:sticky lg:top-28 space-y-6">
              {/* Trending Now Card */}
              <div className={cn(
                "p-4 sm:p-5 rounded-2xl border backdrop-blur-xl transition-all shrink-0",
                isLight
                  ? "bg-white/95 border-slate-200/90 shadow-md shadow-slate-200/40"
                  : "bg-[#0B132B]/90 border-slate-800 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
              )}>
                <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-200 dark:border-white/5">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <h3 className="font-display font-bold text-xs tracking-wider uppercase text-orange-500">
                    Trending Now
                  </h3>
                </div>

                <div className="space-y-3.5">
                  {trendingPosts.map((post, idx) => (
                    <Link
                      key={post.id || idx}
                      to={`/blog/${post.slug}`}
                      state={{ blog: post }}
                      className="group flex gap-3.5 items-start pb-3.5 border-b border-slate-100 dark:border-white/5 last:border-0 last:pb-0"
                    >
                      {/* Fixed Thumbnail Container with Badge */}
                      <div className={cn(
                        "relative w-16 h-16 rounded-xl overflow-hidden shrink-0 flex items-center justify-center transition-transform duration-300 group-hover:scale-105 border",
                        isLight ? "bg-slate-50 border-slate-200/80 shadow-xs" : "bg-[#060B18] border-white/10"
                      )}>
                        <img
                          src={blogService.getFeaturedImageUrl(post.featured_image, post.image_url)}
                          alt={post.title}
                          className="w-full h-full object-cover object-center"
                          onError={handleImageError}
                        />
                        <span className={cn(
                          "absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-black z-20 leading-none shadow-xs",
                          isLight
                            ? "bg-slate-900 text-white"
                            : "bg-brand-600 text-white font-bold"
                        )}>
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className={cn(
                          "font-bold text-xs leading-snug transition-colors line-clamp-2",
                          isLight
                            ? "text-slate-900 group-hover:text-brand-600"
                            : "text-white group-hover:text-brand-400"
                        )}>
                          {post.title}
                        </h4>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-1.5 font-medium">
                          <span>{post.published_at ? format(new Date(post.published_at), 'MMM dd') : 'Recent'}</span>
                          <span>•</span>
                          <span>{getBlogCategoryInfo(post).name}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Tech Advisory Card */}
              <div className={cn(
                "p-4 sm:p-5 rounded-2xl border backdrop-blur-xl transition-all relative overflow-hidden shrink-0",
                isLight
                  ? "bg-white/95 border-slate-200/90 shadow-md shadow-slate-200/40"
                  : "bg-gradient-to-br from-[#0B132B]/95 to-[#060B18]/95 border-brand-500/20 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
              )}>
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className={cn("w-4 h-4", isLight ? "text-brand-600" : "text-brand-400")} />
                  <h3 className={cn(
                    "font-display font-bold text-xs tracking-wider uppercase",
                    isLight ? "text-slate-900" : "text-white"
                  )}>
                    Tech Advisory
                  </h3>
                </div>

                <p className={cn(
                  "text-xs font-normal leading-relaxed mb-4 italic",
                  isLight ? "text-slate-700" : "text-slate-300"
                )}>
                  "Modular architecture, automated testing pipelines, and decoupled UI layers reduce long-term maintenance overhead by over 50% in scalable web applications."
                </p>

                <div className={cn(
                  "pt-3 border-t flex items-center justify-between text-[10px] font-semibold",
                  isLight ? "border-slate-100 text-brand-600" : "border-white/10 text-brand-400"
                )}>
                  <span>Prince IT Engineering</span>
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* ── SHARED CTA SECTION ── */}
      <SharedCTA
        eyebrow="NEED EXPERT HELP?"
        title="Turn Your Technical Ideas into Production-Ready Solutions"
        description="Whether you need software engineering, a modern web application, or a scalable digital solution, our team is ready to help you build and grow."
        primaryButton={{
          text: "Start a Conversation",
          link: "/contact-us"
        }}
        secondaryButton={{
          text: "Explore Our Work",
          link: "/portfolio"
        }}
      />
    </div>
  );
}
